import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, eur } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FileSignature, ExternalLink, ShieldCheck } from "lucide-react";
import { BulkBar, RowActions, RowCheck, useBulkSelect } from "@/components/AdminBulkActions";

export default function AdesioniRegistry() {
  const [data, setData] = useState({ items: [], count_da_lavorare: 0 });
  const [tab, setTab] = useState("da_lavorare");
  const [showArchived, setShowArchived] = useState(false);
  const sel = useBulkSelect();
  const navigate = useNavigate();

  const load = useCallback(() => api.get(`/admin/adesioni${showArchived ? "?include_archived=true" : ""}`).then(({ data }) => { setData(data); sel.clear(); }), [showArchived]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const items = data.items.filter((i) => (tab === "da_lavorare" ? i.da_lavorare : true));

  return (
    <div className="space-y-6" data-testid="adesioni-registry">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Adesioni</h1>
        <p className="mt-1 text-muted-foreground">Adesioni a distanza completate dai clienti (firma OTP verificata). Prendi in carico quelle da lavorare per procedere con l'emissione.</p>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "da_lavorare" ? "default" : "outline"} onClick={() => setTab("da_lavorare")}
          data-testid="adesioni-tab-da-lavorare" className={tab === "da_lavorare" ? "bg-forest hover:bg-forest/90" : ""}>
          Da lavorare
          {data.count_da_lavorare > 0 && <span className="ml-2 rounded-full bg-terracotta px-2 py-0.5 text-xs text-white">{data.count_da_lavorare}</span>}
        </Button>
        <Button size="sm" variant={tab === "tutte" ? "default" : "outline"} onClick={() => setTab("tutte")}
          data-testid="adesioni-tab-tutte" className={tab === "tutte" ? "bg-forest hover:bg-forest/90" : ""}>
          Tutte ({data.items.length})
        </Button>
      </div>

      <Card className="rounded-xl border-border/60">
        <div className="p-3">
          <BulkBar endpoint="/admin/adesioni/bulk" selected={sel.selected} onDone={load}
            showArchived={showArchived} onToggleArchived={() => setShowArchived((s) => !s)} entityLabel="adesioni" />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><Checkbox checked={items.length > 0 && sel.count >= items.length} onCheckedChange={() => sel.toggleAll(items.map((x) => x.quote_id))} data-testid="select-all" /></TableHead>
              <TableHead>Contraente</TableHead>
              <TableHead>Prodotto</TableHead>
              <TableHead>Premio</TableHead>
              <TableHead>Data adesione</TableHead>
              <TableHead>Firma</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground" data-testid="adesioni-empty">Nessuna adesione {tab === "da_lavorare" ? "da lavorare" : ""}.</TableCell></TableRow>
            ) : items.map((i) => (
              <TableRow key={i.quote_id} data-testid={`adesione-row-${i.quote_id}`}>
                <TableCell><RowCheck id={i.quote_id} selected={sel.isSelected(i.quote_id)} onToggle={sel.toggle} /></TableCell>
                <TableCell className="font-medium text-forest">{i.contraente}</TableCell>
                <TableCell>{i.prodotto}</TableCell>
                <TableCell>{eur(i.premio)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{i.accepted_at ? new Date(i.accepted_at).toLocaleString("it-IT") : "—"}</TableCell>
                <TableCell>{i.firma ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" />{i.firma}</span> : "—"}</TableCell>
                <TableCell>
                  {i.da_lavorare
                    ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Da lavorare</Badge>
                    : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Emessa/chiusa</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="outline" data-testid={`adesione-open-${i.quote_id}`}
                      onClick={() => navigate(`/admin/preventivi?open=${i.quote_id}`)} className="gap-1.5">
                      <ExternalLink className="h-3.5 w-3.5" /> Apri
                    </Button>
                    <RowActions endpoint="/admin/adesioni/bulk" id={i.quote_id} archived={showArchived} onDone={load} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
